import { Ionicons } from '@expo/vector-icons';
import { useNavigation, useRoute } from '@react-navigation/native';
import React, { useEffect, useState } from 'react';
import { Alert, ScrollView, StyleSheet, View } from 'react-native';
import { ActivityIndicator, Button, Card, Chip, DataTable, Divider, Text } from 'react-native-paper';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

const BILLETES = [
  { key: 'billete_500', label: 'L. 500', valor: 500 },
  { key: 'billete_200', label: 'L. 200', valor: 200 },
  { key: 'billete_100', label: 'L. 100', valor: 100 },
  { key: 'billete_50', label: 'L. 50', valor: 50 },
  { key: 'billete_20', label: 'L. 20', valor: 20 },
  { key: 'billete_10', label: 'L. 10', valor: 10 },
  { key: 'billete_5', label: 'L. 5', valor: 5 },
  { key: 'billete_2', label: 'L. 2', valor: 2 },
  { key: 'billete_1', label: 'L. 1', valor: 1 },
];
const MONEDAS = [
  { key: 'moneda_5', label: 'L. 5.00', valor: 5 },
  { key: 'moneda_2', label: 'L. 2.00', valor: 2 },
  { key: 'moneda_1', label: 'L. 1.00', valor: 1 },
  { key: 'moneda_050', label: 'L. 0.50', valor: 0.50 },
  { key: 'moneda_020', label: 'L. 0.20', valor: 0.20 },
  { key: 'moneda_010', label: 'L. 0.10', valor: 0.10 },
  { key: 'moneda_005', label: 'L. 0.05', valor: 0.05 },
];

const DetalleCierreScreen = () => {
  const navigation = useNavigation();
  const route = useRoute();
  const { user } = useAuth();
  const { cierreId } = route.params || {};
  const [cierre, setCierre] = useState(null);
  const [loading, setLoading] = useState(true);

  const canApprove = user && ['admin', 'supervisor', 'gerente'].includes(user.rol);

  useEffect(() => { loadCierre(); }, [cierreId]);

  const loadCierre = async () => {
    try {
      setLoading(true);
      const data = await api.getCierre(cierreId);
      setCierre(data);
    } catch (error) {
      console.error('Error:', error);
      Alert.alert('Error', 'No se pudo cargar el cierre');
    } finally {
      setLoading(false);
    }
  };

  const handleCompletar = () => {
    Alert.alert('Aprobar Cierre', '¿Desea aprobar y completar este cierre?', [
      { text: 'Cancelar', style: 'cancel' },
      {
        text: 'Aprobar', onPress: async () => {
          try {
            await api.completarCierre(cierreId);
            Alert.alert('Aprobado', 'Cierre completado exitosamente');
            loadCierre();
          } catch (error) {
            Alert.alert('Error', error.message);
          }
        }
      }
    ]);
  };

  const handleRechazar = () => {
    Alert.prompt ? Alert.prompt('Rechazar Cierre', 'Motivo del rechazo:', async (motivo) => {
      try {
        await api.rechazarCierre(cierreId, { motivo });
        Alert.alert('Rechazado', 'Cierre rechazado');
        loadCierre();
      } catch (error) { Alert.alert('Error', error.message); }
    }) : Alert.alert('Rechazar', '¿Confirmar rechazo?', [
      { text: 'Cancelar', style: 'cancel' },
      {
        text: 'Rechazar', style: 'destructive', onPress: async () => {
          try {
            await api.rechazarCierre(cierreId, { motivo: 'Rechazado por supervisor' });
            loadCierre();
          } catch (error) { Alert.alert('Error', error.message); }
        }
      }
    ]);
  };

  const handleAutorizarDevoluciones = (autorizar) => {
    Alert.alert(
      autorizar ? 'Autorizar Devoluciones' : 'Rechazar Devoluciones',
      `¿Confirma ${autorizar ? 'autorizar' : 'rechazar'} las devoluciones de este cierre?`,
      [
        { text: 'Cancelar', style: 'cancel' },
        {
          text: 'Confirmar', onPress: async () => {
            try {
              await api.autorizarDevolucionesCierre(cierreId, { autorizar });
              Alert.alert('Listo', autorizar ? 'Devoluciones autorizadas' : 'Devoluciones rechazadas');
              loadCierre();
            } catch (error) { Alert.alert('Error', error.message); }
          }
        }
      ]
    );
  };

  const getEstadoStyle = (estado) => {
    switch (estado) {
      case 'completado': return { bg: '#ccffcc', text: '#2e7d32' };
      case 'pendiente': return { bg: '#fff0cc', text: '#e65100' };
      case 'rechazado': return { bg: '#ffcccc', text: '#c62828' };
      default: return { bg: '#e0e0e0', text: '#555' };
    }
  };

  if (loading) {
    return (
      <View style={styles.loaderContainer}>
        <ActivityIndicator size="large" color="#0066cc" />
        <Text style={styles.loaderText}>Cargando cierre...</Text>
      </View>
    );
  }

  if (!cierre) {
    return (
      <View style={styles.loaderContainer}>
        <Ionicons name="alert-circle" size={48} color="#999" />
        <Text style={styles.loaderText}>No se encontró el cierre</Text>
      </View>
    );
  }

  const estadoStyle = getEstadoStyle(cierre.estado);

  return (
    <ScrollView style={styles.container}>
      {/* Header */}
      <Card style={styles.headerCard}>
        <Card.Content>
          <View style={styles.headerRow}>
            <View>
              <Text style={styles.headerTitle}>Cierre #{cierre.id}</Text>
              <Text style={styles.headerSubtitle}>{cierre.fecha} - {cierre.hora_cierre || '--:--'}</Text>
              <Text style={styles.headerUser}>{cierre.vendedor || cierre.usuario_nombre}</Text>
            </View>
            <View style={styles.headerBadges}>
              <Chip mode="outlined" style={[styles.estadoChip, { backgroundColor: estadoStyle.bg }]} textStyle={{ color: estadoStyle.text }}>
                {cierre.estado.charAt(0).toUpperCase() + cierre.estado.slice(1)}
              </Chip>
              <Chip mode="outlined" style={styles.tipoChip}>
                {cierre.tipo === 'repartidor' ? 'Repartidor' : cierre.tipo === 'consolidado' ? 'Consolidado' : 'Vendedor'}
              </Chip>
              {cierre.es_automatico === 1 && (
                <Chip mode="outlined" style={styles.autoChip} textStyle={{ fontSize: 10 }}>Auto</Chip>
              )}
            </View>
          </View>
        </Card.Content>
      </Card>

      {/* Resumen financiero */}
      <Card style={styles.card}>
        <Card.Content>
          <Text style={styles.sectionTitle}>Resumen Financiero</Text>
          <View style={styles.resumenGrid}>
            <View style={styles.resumenItem}>
              <Ionicons name="cart" size={20} color="#1976d2" />
              <Text style={styles.resumenLabel}>Ventas</Text>
              <Text style={styles.resumenValue}>L. {(cierre.total_ventas || 0).toFixed(2)}</Text>
              <Text style={styles.resumenCount}>{cierre.cantidad_ventas || 0}</Text>
            </View>
            <View style={styles.resumenItem}>
              <Ionicons name="checkmark-circle" size={20} color="#388e3c" />
              <Text style={styles.resumenLabel}>Entregas</Text>
              <Text style={styles.resumenValue}>L. {(cierre.total_entregas || 0).toFixed(2)}</Text>
              <Text style={styles.resumenCount}>{cierre.cantidad_entregas || 0}</Text>
            </View>
            <View style={styles.resumenItem}>
              <Ionicons name="return-down-back" size={20} color="#d32f2f" />
              <Text style={styles.resumenLabel}>Devoluciones</Text>
              <Text style={[styles.resumenValue, { color: '#d32f2f' }]}>-L. {(cierre.total_devoluciones || 0).toFixed(2)}</Text>
              <Text style={styles.resumenCount}>{cierre.cantidad_devoluciones || 0}</Text>
            </View>
          </View>
          <View style={styles.netoRow}>
            <Text style={styles.netoLabel}>Total Neto:</Text>
            <Text style={styles.netoValue}>L. {(cierre.total_neto || 0).toFixed(2)}</Text>
          </View>
        </Card.Content>
      </Card>

      {/* Desglose método de pago */}
      <Card style={styles.card}>
        <Card.Content>
          <Text style={styles.sectionTitle}>Desglose por Método de Pago</Text>
          <DataTable>
            <DataTable.Header>
              <DataTable.Title>Método</DataTable.Title>
              <DataTable.Title numeric>Monto</DataTable.Title>
            </DataTable.Header>
            <DataTable.Row><DataTable.Cell>Efectivo</DataTable.Cell><DataTable.Cell numeric>L. {(cierre.monto_efectivo || 0).toFixed(2)}</DataTable.Cell></DataTable.Row>
            <DataTable.Row><DataTable.Cell>Tarjeta</DataTable.Cell><DataTable.Cell numeric>L. {(cierre.monto_tarjeta || 0).toFixed(2)}</DataTable.Cell></DataTable.Row>
            <DataTable.Row><DataTable.Cell>Transferencia</DataTable.Cell><DataTable.Cell numeric>L. {(cierre.monto_transferencia || 0).toFixed(2)}</DataTable.Cell></DataTable.Row>
            <DataTable.Row><DataTable.Cell>Crédito</DataTable.Cell><DataTable.Cell numeric>L. {(cierre.monto_credito || 0).toFixed(2)}</DataTable.Cell></DataTable.Row>
          </DataTable>
        </Card.Content>
      </Card>

      {/* Cuadre repartidor */}
      {cierre.tipo === 'repartidor' && cierre.cuadre_correcto !== null && (
        <Card style={[styles.card, { borderLeftWidth: 4, borderLeftColor: cierre.cuadre_correcto ? '#4caf50' : '#f44336' }]}>
          <Card.Content>
            <Text style={styles.sectionTitle}>
              <Ionicons name="swap-horizontal" size={16} /> Cuadre del Repartidor
            </Text>
            <View style={styles.cuadreRow}>
              <Text>Diferencia:</Text>
              <Text style={[styles.cuadreVal, { color: cierre.cuadre_correcto ? '#4caf50' : '#f44336' }]}>
                {cierre.diferencia_cuadre >= 0 ? '+' : ''}L. {(cierre.diferencia_cuadre || 0).toFixed(2)}
              </Text>
            </View>
            <View style={styles.cuadreRow}>
              <Text>Estado:</Text>
              <Chip mode="outlined" style={{ backgroundColor: cierre.cuadre_correcto ? '#e8f5e9' : '#ffebee' }}>
                {cierre.cuadre_correcto ? 'Cuadre correcto' : 'Descuadrado'}
              </Chip>
            </View>
            {cierre.justificacion_diferencia && (
              <View style={styles.justificacionBox}>
                <Text style={styles.justificacionLabel}>Justificación:</Text>
                <Text style={styles.justificacionText}>{cierre.justificacion_diferencia}</Text>
              </View>
            )}
          </Card.Content>
        </Card>
      )}

      {/* Arqueo de caja */}
      {cierre.arqueo && (
        <Card style={[styles.card, { borderLeftWidth: 4, borderLeftColor: '#6a1b9a' }]}>
          <Card.Content>
            <Text style={styles.sectionTitle}>
              <Ionicons name="cash" size={16} color="#6a1b9a" /> Arqueo de Caja
            </Text>

            <Text style={styles.denominacionSecTitle}>Billetes</Text>
            {BILLETES.map(b => {
              const cant = cierre.arqueo[b.key] || 0;
              if (cant === 0) return null;
              return (
                <View key={b.key} style={styles.arqueoDetRow}>
                  <Text style={styles.arqueoDetLabel}>{b.label}</Text>
                  <Text style={styles.arqueoDetCant}>x {cant}</Text>
                  <Text style={styles.arqueoDetTotal}>L. {(cant * b.valor).toFixed(2)}</Text>
                </View>
              );
            })}
            <View style={styles.subtotalRow}>
              <Text style={styles.subtotalLabel}>Subtotal Billetes:</Text>
              <Text style={styles.subtotalValue}>L. {parseFloat(cierre.arqueo.total_billetes || 0).toFixed(2)}</Text>
            </View>

            <Divider style={{ marginVertical: 6 }} />

            <Text style={styles.denominacionSecTitle}>Monedas</Text>
            {MONEDAS.map(m => {
              const cant = cierre.arqueo[m.key] || 0;
              if (cant === 0) return null;
              return (
                <View key={m.key} style={styles.arqueoDetRow}>
                  <Text style={styles.arqueoDetLabel}>{m.label}</Text>
                  <Text style={styles.arqueoDetCant}>x {cant}</Text>
                  <Text style={styles.arqueoDetTotal}>L. {(cant * m.valor).toFixed(2)}</Text>
                </View>
              );
            })}
            <View style={styles.subtotalRow}>
              <Text style={styles.subtotalLabel}>Subtotal Monedas:</Text>
              <Text style={styles.subtotalValue}>L. {parseFloat(cierre.arqueo.total_monedas || 0).toFixed(2)}</Text>
            </View>

            <Divider style={{ marginVertical: 8 }} />

            <View style={styles.arqueoTotalFinal}>
              <View style={styles.arqueoFinalRow}>
                <Text style={styles.arqueoFinalLabel}>Total Arqueo:</Text>
                <Text style={styles.arqueoFinalVal}>L. {parseFloat(cierre.arqueo.total_arqueo || 0).toFixed(2)}</Text>
              </View>
              <View style={styles.arqueoFinalRow}>
                <Text style={styles.arqueoFinalLabel}>Esperado:</Text>
                <Text>L. {parseFloat(cierre.arqueo.total_esperado || 0).toFixed(2)}</Text>
              </View>
              <View style={[styles.arqueoFinalRow, { backgroundColor: Math.abs(parseFloat(cierre.arqueo.diferencia || 0)) <= 5 ? '#e8f5e9' : '#ffebee', borderRadius: 6, padding: 8 }]}>
                <Text style={styles.arqueoFinalLabel}>Diferencia:</Text>
                <Text style={{ fontWeight: 'bold', fontSize: 16, color: Math.abs(parseFloat(cierre.arqueo.diferencia || 0)) <= 5 ? '#2e7d32' : '#c62828' }}>
                  {parseFloat(cierre.arqueo.diferencia || 0) >= 0 ? '+' : ''}L. {parseFloat(cierre.arqueo.diferencia || 0).toFixed(2)}
                </Text>
              </View>
            </View>

            {cierre.arqueo.justificacion && (
              <View style={styles.justificacionBox}>
                <Text style={styles.justificacionLabel}>Justificación:</Text>
                <Text style={styles.justificacionText}>{cierre.arqueo.justificacion}</Text>
              </View>
            )}
          </Card.Content>
        </Card>
      )}

      {/* Devoluciones y autorización */}
      {cierre.tiene_devoluciones === 1 && (
        <Card style={[styles.card, { borderLeftWidth: 4, borderLeftColor: '#ff9800' }]}>
          <Card.Content>
            <Text style={styles.sectionTitle}>
              <Ionicons name="return-down-back" size={16} color="#ff9800" /> Devoluciones
            </Text>
            <View style={styles.cuadreRow}>
              <Text>Estado autorización:</Text>
              <Chip mode="outlined" style={{
                backgroundColor: cierre.devoluciones_autorizadas === 1 ? '#e8f5e9' :
                  cierre.devoluciones_autorizadas === 0 ? '#ffebee' : '#fff0cc'
              }}>
                {cierre.devoluciones_autorizadas === 1 ? 'Autorizadas' :
                  cierre.devoluciones_autorizadas === 0 ? 'Rechazadas' : 'Pendiente'}
              </Chip>
            </View>
            {cierre.autorizado_por_nombre && (
              <Text style={styles.metaText}>Autorizado por: {cierre.autorizado_por_nombre}</Text>
            )}
            {cierre.autorizacion_notas && (
              <Text style={styles.metaText}>Notas: {cierre.autorizacion_notas}</Text>
            )}

            {canApprove && cierre.devoluciones_autorizadas === null && (
              <View style={styles.authButtons}>
                <Button mode="contained" onPress={() => handleAutorizarDevoluciones(true)} style={[styles.authBtn, { backgroundColor: '#4caf50' }]} icon="check">
                  Autorizar
                </Button>
                <Button mode="contained" onPress={() => handleAutorizarDevoluciones(false)} style={[styles.authBtn, { backgroundColor: '#f44336' }]} icon="close">
                  Rechazar
                </Button>
              </View>
            )}
          </Card.Content>
        </Card>
      )}

      {/* Ventas del cierre */}
      {cierre.ventas && cierre.ventas.length > 0 && (
        <Card style={styles.card}>
          <Card.Content>
            <Text style={styles.sectionTitle}>Ventas del Cierre ({cierre.ventas.length})</Text>
            <DataTable>
              <DataTable.Header>
                <DataTable.Title>ID</DataTable.Title>
                <DataTable.Title>Cliente</DataTable.Title>
                <DataTable.Title>Estado</DataTable.Title>
                <DataTable.Title numeric>Total</DataTable.Title>
              </DataTable.Header>
              {cierre.ventas.map(v => (
                <DataTable.Row key={v.id}>
                  <DataTable.Cell>#{v.venta_id}</DataTable.Cell>
                  <DataTable.Cell>{(v.cliente_nombre || '').substring(0, 15)}</DataTable.Cell>
                  <DataTable.Cell>{v.estado_venta}</DataTable.Cell>
                  <DataTable.Cell numeric>L. {parseFloat(v.total || 0).toFixed(2)}</DataTable.Cell>
                </DataTable.Row>
              ))}
            </DataTable>
          </Card.Content>
        </Card>
      )}

      {/* Observaciones */}
      {cierre.observaciones && (
        <Card style={styles.card}>
          <Card.Content>
            <Text style={styles.sectionTitle}>Observaciones</Text>
            <Text style={styles.observaciones}>{cierre.observaciones}</Text>
          </Card.Content>
        </Card>
      )}

      {/* Info de auditoría */}
      <Card style={styles.card}>
        <Card.Content>
          <Text style={styles.sectionTitle}>Información</Text>
          <View style={styles.infoRow}><Text style={styles.infoLabel}>Creado por:</Text><Text>{cierre.creado_por_nombre || '--'}</Text></View>
          <View style={styles.infoRow}><Text style={styles.infoLabel}>Creado en:</Text><Text>{cierre.creado_en || '--'}</Text></View>
          <View style={styles.infoRow}><Text style={styles.infoLabel}>Cerrado en:</Text><Text>{cierre.cerrado_en || '--'}</Text></View>
        </Card.Content>
      </Card>

      {/* Acciones */}
      {canApprove && cierre.estado === 'pendiente' && (
        <View style={styles.actionsContainer}>
          <Button mode="contained" onPress={handleCompletar} style={[styles.actionBtn, { backgroundColor: '#4caf50' }]} icon="check-circle">
            Aprobar Cierre
          </Button>
          <Button mode="contained" onPress={handleRechazar} style={[styles.actionBtn, { backgroundColor: '#f44336' }]} icon="close-circle">
            Rechazar
          </Button>
        </View>
      )}

      <View style={{ height: 30 }} />
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#f5f5f5', padding: 10 },
  headerCard: { marginBottom: 12, elevation: 3, backgroundColor: '#fff' },
  headerRow: { flexDirection: 'row', justifyContent: 'space-between' },
  headerTitle: { fontSize: 20, fontWeight: 'bold' },
  headerSubtitle: { fontSize: 14, color: '#666' },
  headerUser: { fontSize: 14, color: '#333', marginTop: 2 },
  headerBadges: { alignItems: 'flex-end' },
  estadoChip: { marginBottom: 4 },
  tipoChip: { marginBottom: 4, backgroundColor: '#e3f2fd' },
  autoChip: { backgroundColor: '#f3e5f5' },
  card: { marginBottom: 12, elevation: 2 },
  sectionTitle: { fontSize: 16, fontWeight: 'bold', color: '#0066cc', marginBottom: 10 },
  resumenGrid: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 8 },
  resumenItem: { flex: 1, alignItems: 'center', padding: 10, backgroundColor: '#f9f9f9', borderRadius: 6, marginHorizontal: 4 },
  resumenLabel: { fontSize: 11, color: '#666', marginTop: 4 },
  resumenValue: { fontSize: 15, fontWeight: 'bold', marginTop: 2 },
  resumenCount: { fontSize: 10, color: '#999' },
  netoRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#e8f5e9', padding: 12, borderRadius: 8 },
  netoLabel: { fontSize: 16, fontWeight: 'bold' },
  netoValue: { fontSize: 20, fontWeight: 'bold', color: '#2e7d32' },
  cuadreRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingVertical: 6 },
  cuadreVal: { fontSize: 16, fontWeight: 'bold' },
  justificacionBox: { backgroundColor: '#fff3e0', padding: 10, borderRadius: 6, marginTop: 8 },
  justificacionLabel: { fontWeight: 'bold', color: '#e65100', marginBottom: 4 },
  justificacionText: { color: '#333' },
  // Arqueo detalle
  denominacionSecTitle: { fontSize: 13, fontWeight: 'bold', color: '#555', marginBottom: 4, marginTop: 4 },
  arqueoDetRow: { flexDirection: 'row', alignItems: 'center', paddingVertical: 3 },
  arqueoDetLabel: { width: 70, fontWeight: '600' },
  arqueoDetCant: { width: 50, textAlign: 'center', color: '#666' },
  arqueoDetTotal: { flex: 1, textAlign: 'right', fontWeight: '500' },
  subtotalRow: { flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 6, backgroundColor: '#f5f5f5', borderRadius: 4, paddingHorizontal: 8, marginTop: 4 },
  subtotalLabel: { fontWeight: 'bold', color: '#555' },
  subtotalValue: { fontWeight: 'bold' },
  arqueoTotalFinal: { marginTop: 4 },
  arqueoFinalRow: { flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 4, paddingHorizontal: 4 },
  arqueoFinalLabel: { fontSize: 14, fontWeight: 'bold' },
  arqueoFinalVal: { fontSize: 17, fontWeight: 'bold', color: '#6a1b9a' },
  metaText: { fontSize: 13, color: '#666', marginTop: 4 },
  authButtons: { flexDirection: 'row', justifyContent: 'space-between', marginTop: 12 },
  authBtn: { flex: 1, marginHorizontal: 4 },
  observaciones: { fontStyle: 'italic', color: '#555' },
  infoRow: { flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 4 },
  infoLabel: { fontWeight: 'bold', color: '#555' },
  actionsContainer: { flexDirection: 'row', justifyContent: 'space-between', marginTop: 8 },
  actionBtn: { flex: 1, marginHorizontal: 4 },
  loaderContainer: { flex: 1, justifyContent: 'center', alignItems: 'center', paddingTop: 50 },
  loaderText: { marginTop: 10, color: '#666' },
});

export default DetalleCierreScreen;
